import { faArrowDown, faArrowUp, faTrash } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { ArticleStatusRule, NEW_ARTICLE_STATUS_RULE, parseArticleStatusRules } from './articleStatusRules.ts';
import Button from '../../core/components/Button.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import TagsField from '../../core/components/Fields/TagsField.tsx';
import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { useToaster } from '../../core/components/Toast.tsx';

const ArrowUpIcon = withIcon(faArrowUp);
const ArrowDownIcon = withIcon(faArrowDown);
const DeleteIcon = withIcon(faTrash);

const PREFIX = 'configPage.document.articleStatusRules';

interface ArticleStatusRuleCardProps {
    rule: ArticleStatusRule;
    onRuleChange: (changes: Partial<ArticleStatusRule>) => void;
    onBlur?: () => void;
    showErrors: boolean;
}

const ArticleStatusRuleCard = ({ rule, onRuleChange, onBlur, showErrors }: ArticleStatusRuleCardProps) => {
    const { t } = useTranslation('config');

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-lg border border-solid border-line-weak p-4 min-w-0 flex-1">
            <FormField
                className="md:col-span-2"
                label={t(`${PREFIX}.statuses`)}
                helpText={t(`${PREFIX}.statusesHelpText`)}
                required
                error={
                    showErrors && !(rule.statuses || []).length
                        ? { message: t(`${PREFIX}.statusesRequired`) }
                        : undefined
                }
            >
                <TagsField
                    value={rule.statuses || []}
                    placeholder={t(`${PREFIX}.statusesPlaceholder`)}
                    onValueChange={(statuses) => onRuleChange({ statuses: statuses as string[] })}
                    onBlur={onBlur}
                />
            </FormField>

            <FormField label={t(`${PREFIX}.isBlocking`)} helpText={t(`${PREFIX}.isBlockingHelpText`)}>
                <BooleanField
                    value={rule.is_blocking ? 'true' : ''}
                    onValueChange={(is_blocking) => onRuleChange({ is_blocking: !!is_blocking })}
                />
            </FormField>
        </div>
    );
};

interface ArticleStatusRulesFieldProps {
    value?: ArticleStatusRule[] | null;
    onValueChange: (value: ArticleStatusRule[]) => void;
    onBlur?: () => void;
    showErrors?: boolean;
}

// Mirrors the delivery date RuleList (core/components/Rules): ordered rules, row actions, copy/paste, add
const ArticleStatusRulesField = ({
    value,
    onValueChange,
    onBlur,
    showErrors = false,
}: ArticleStatusRulesFieldProps) => {
    const { t } = useTranslation('config');
    const { t: tCore } = useTranslation('core');
    const { publishToast } = useToaster();
    const rules = value || [];

    const updateRule = (index: number, changes: Partial<ArticleStatusRule>) =>
        onValueChange(rules.map((rule, i) => (i === index ? { ...rule, ...changes } : rule)));

    const moveRule = (index: number, offset: number) => {
        const newRules = [...rules];
        [newRules[index], newRules[index + offset]] = [newRules[index + offset], newRules[index]];
        onValueChange(newRules);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(JSON.stringify(rules));
        publishToast({ status: 'success', description: tCore('components.rules.copySuccessToast') });
    };

    const handlePaste = async () => {
        try {
            const pastedRules = parseArticleStatusRules(await navigator.clipboard.readText());
            onValueChange([...rules, ...pastedRules]);
            publishToast({ status: 'success', description: tCore('components.rules.pasteSuccessToast') });
        } catch (e) {
            console.error(e);
            publishToast({ status: 'error', description: t(`${PREFIX}.pasteErrorToast`) });
        }
    };

    return (
        <div className="flex flex-col gap-4 w-full">
            {rules.map((rule, index) => (
                <div className="flex gap-2" key={index}>
                    <ArticleStatusRuleCard
                        rule={rule}
                        onRuleChange={(changes) => updateRule(index, changes)}
                        onBlur={onBlur}
                        showErrors={showErrors}
                    />
                    <div className="flex flex-col justify-start gap-2">
                        <Button
                            type="button"
                            aria-label={t(`${PREFIX}.removeRule`)}
                            onClick={() => onValueChange(rules.filter((_, i) => i !== index))}
                        >
                            <DeleteIcon />
                        </Button>
                        <Button
                            type="button"
                            aria-label={t(`${PREFIX}.moveRuleUp`)}
                            disabled={index === 0}
                            onClick={() => moveRule(index, -1)}
                        >
                            <ArrowUpIcon />
                        </Button>
                        <Button
                            type="button"
                            aria-label={t(`${PREFIX}.moveRuleDown`)}
                            disabled={index === rules.length - 1}
                            onClick={() => moveRule(index, 1)}
                        >
                            <ArrowDownIcon />
                        </Button>
                    </div>
                </div>
            ))}

            <div className="flex items-center gap-2 text-content-medium text-sm">
                <Button type="button" variant="ghost" onClick={handleCopy} disabled={rules.length === 0}>
                    {tCore('components.rules.copyButton')}
                </Button>
                <span>-</span>
                <Button type="button" variant="ghost" onClick={handlePaste}>
                    {tCore('components.rules.pasteButton')}
                </Button>
            </div>

            <Button type="button" onClick={() => onValueChange([...rules, { ...NEW_ARTICLE_STATUS_RULE }])}>
                {tCore('components.rules.addRuleButton')}
            </Button>

            <span className="text-content-medium text-xs">{t(`${PREFIX}.helpText`)}</span>
        </div>
    );
};

export default ArticleStatusRulesField;
